// deps
import React, { ReactElement, memo, useEffect, useRef } from "react";

// components
import { Portal } from "@core-ui/components/portal";
import { OverflowHidden } from "@core-ui/components/side-effects";
import { OverlayStyled } from "./styles";

// hooks
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

interface UpdateProgressLoaderProps {
    /**
     * Optional. Fired once when the loader unmounts (the overlay is removed), with how long it was
     * on screen in ms, so a caller can measure how long the cart stayed blocked. Most callers omit it.
     */
    onRemoved?: (visibleForMs: number) => void;
}

function UpdateProgressLoader({ onRemoved }: UpdateProgressLoaderProps): ReactElement {
    // Keep the latest onRemoved in a ref so the unmount-time call below never uses a stale
    // closure if the prop changes while the overlay stays mounted (e.g. once the overlay becomes
    // non-blocking and a cart mutation can overlap a background refresh).
    const onRemovedRef = useRef(onRemoved);
    useEffect(() => {
        onRemovedRef.current = onRemoved;
    }, [onRemoved]);

    useEffectOnce(() => {
        const shownAt = Date.now();
        return () => onRemovedRef.current?.(Date.now() - shownAt);
    });

    return (
        <Portal>
            <OverflowHidden>
                <OverlayStyled visible={true} />
            </OverflowHidden>
        </Portal>
    );
}

export default memo(UpdateProgressLoader);
